import { useCallback, useEffect, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { ScrollArea } from '@/components/ui/scroll-area'

import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet'
import { Calendar, SlidersHorizontal } from 'lucide-react'
import { Separator } from '@/components/ui/separator'
import { EconomicCycleFilter } from './EconomicCycleFilter'
import { useCurrencyPaymentsFilters } from '../hooks/useCurrencyPaymentsFilters'
import { PaymentStatusFilter } from './PaymentStatusFilter '
import { CurrencyPaymentStatus } from '@/interfaces/billing'

// Define local advanced filters type - only the advanced filters we manage locally
interface LocalAdvancedFilters {
  economicCycleIds: string[]
  status: string | null
}

const CleanSelectionButton = ({
  show = false,
  onClick,
  label = 'Limpiar selección',
}: {
  show: boolean
  onClick: () => void
  label?: string
}) => {
  if (!show) return null
  return (
    <div className="mt-2">
      <button
        className="text-sm text-red-600 hover:text-red-800"
        onClick={onClick}
        type="button"
      >
        {label}
      </button>
    </div>
  )
}

// Helper function to create default local filters
const createDefaultLocalFilters = (): LocalAdvancedFilters => ({
  economicCycleIds: [],
  status: null,
})

// Helper function to extract advanced filters from global filters
const resolveSelectedStatus = (
  f: ReturnType<typeof useCurrencyPaymentsFilters>['filters']
): CurrencyPaymentStatus | null => {
  if (f.isARefund) return 'REFUND'
  if (f.isPrepaidPayment) return 'PREPAID_PAYMENT'
  if (f.isPartialPayment) return 'PARTIAL_PAYMENT'
  if (f.paymentConfirmed) return 'CONFIRMED_PAYMENT'
  return null
}

const extractAdvancedFilters = (
  globalFilters: ReturnType<typeof useCurrencyPaymentsFilters>['filters']
): LocalAdvancedFilters => ({
  economicCycleIds: globalFilters.economicCycleIds ?? [],
  status: resolveSelectedStatus(globalFilters),
})

export const AdvancedFilters = () => {
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false)

  const {
    filters: globalFilters,
    updateEconomicCycleIds,
    updateStatus,
  } = useCurrencyPaymentsFilters()

  // Local state for advanced filters
  const [localFilters, setLocalFilters] = useState<LocalAdvancedFilters>(() =>
    extractAdvancedFilters(globalFilters)
  )

  // Sync local filters when global filters change externally (e.g., from saved filters)
  useEffect(() => {
    if (!isFilterDrawerOpen) {
      setLocalFilters(extractAdvancedFilters(globalFilters))
    }
  }, [globalFilters, isFilterDrawerOpen])

  // Reset local filters when drawer opens to match current global state
  useEffect(() => {
    if (isFilterDrawerOpen) {
      setLocalFilters(extractAdvancedFilters(globalFilters))
    }
  }, [isFilterDrawerOpen, globalFilters])

  // Local filter update functions
  const updateLocalEconomicCycles = useCallback((ids: string[]) => {
    setLocalFilters((prev) => ({ ...prev, economicCycleIds: ids }))
  }, [])

  const updateLocalStatus = useCallback((status: string | null) => {
    setLocalFilters((prev) => ({ ...prev, status }))
  }, [])

  // Local clear functions
  const clearLocalEconomicCyclesFilter = useCallback(() => {
    setLocalFilters((prev) => ({ ...prev, economicCycleIds: [] }))
  }, [])

  const clearLocalStatusFilter = useCallback(() => {
    setLocalFilters((prev) => ({ ...prev, status: null }))
  }, [])

  // Apply local filters to global state
  const handleApplyFilters = useCallback(() => {
    // Apply all local filters to global state using individual update functions

    updateEconomicCycleIds(localFilters.economicCycleIds)
    updateStatus(localFilters.status)

    setIsFilterDrawerOpen(false)
  }, [localFilters, updateEconomicCycleIds, updateStatus])

  // Clear all local filters
  const handleClearAllFilters = useCallback(() => {
    setLocalFilters(createDefaultLocalFilters())
  }, [])

  // Count active advanced filters based on global state (for the badge)
  const getActiveAdvancedFiltersCount = () => {
    let count = 0
    if (
      globalFilters.economicCycleIds &&
      globalFilters.economicCycleIds.length > 0
    )
      count++
    if (
      globalFilters.isARefund ||
      globalFilters.isPrepaidPayment ||
      globalFilters.isPartialPayment ||
      globalFilters.paymentConfirmed
    ) {
      count++
    }
    return count
  }

  return (
    <Sheet onOpenChange={setIsFilterDrawerOpen} open={isFilterDrawerOpen}>
      <SheetTrigger asChild>
        <Button
          aria-label="Filtros avanzados"
          className="gap-1.5"
          variant="outline"
        >
          <SlidersHorizontal className="h-4 w-4" />
          <span className="hidden sm:inline">Filtros avanzados</span>
          {getActiveAdvancedFiltersCount() > 0 && (
            <Badge className="ml-1 rounded-full px-1.5" variant="secondary">
              {getActiveAdvancedFiltersCount()}
            </Badge>
          )}
        </Button>
      </SheetTrigger>
      <SheetContent className="sm:max-w-lg">
        <SheetHeader>
          <SheetTitle>Filtros avanzados</SheetTitle>
          <SheetDescription>
            Configura filtros específicos para encontrar exactamente lo que
            buscas.
          </SheetDescription>
        </SheetHeader>

        <ScrollArea className="h-[calc(100vh-180px)] pr-4">
          <div className="space-y-6 py-6">
            <Separator />

            {/* Economic Cycles */}
            <div className="grid grid-cols-1 gap-4">
              <div className="space-y-2">
                <EconomicCycleFilter
                  icon={<Calendar className="h-4 w-4" />}
                  onSelectionChange={(ids: number[]) =>
                    updateLocalEconomicCycles(ids.map((id) => id.toString()))
                  }
                  placeholder="Seleccionar ciclo económico..."
                  selectedValues={localFilters.economicCycleIds.map((id) =>
                    parseInt(id)
                  )}
                  title="Ciclos económicos"
                />
                <CleanSelectionButton
                  label="Limpiar selección"
                  onClick={clearLocalEconomicCyclesFilter}
                  show={localFilters.economicCycleIds.length > 0}
                />
              </div>
            </div>

            <Separator />

            <div className="grid grid-cols-1 gap-4">
              <div className="space-y-2">
                <PaymentStatusFilter
                  onPaymentStatusChange={(value: string | null) =>
                    updateLocalStatus(value === null ? null : value.toString())
                  }
                  selectedPaymentStatus={localFilters.status}
                />
                <CleanSelectionButton
                  label="Limpiar selección"
                  onClick={() => clearLocalStatusFilter()}
                  show={localFilters.status != null}
                />
              </div>
            </div>

            <Separator />
          </div>
        </ScrollArea>

        <SheetFooter className="flex-row justify-between gap-2 sm:justify-between">
          <Button
            className="flex-1"
            onClick={handleClearAllFilters}
            variant="outline"
          >
            Limpiar selección
          </Button>
          <Button className="flex-1" onClick={handleApplyFilters}>
            Aplicar
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}
